/* eslint-disable relay/unused-fields -- the queried style fields are read in ./helpers via getVariantStyledUrl */
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";
import "swiper/css";
import { Navigation } from "swiper/modules";
import { SwiperSlide } from "swiper/react";

import type { ResponsiveVariants } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { BaseImage } from "scmp-app/components/common/base-image";
import { useResponsiveImage } from "scmp-app/components/common/base-image/hooks";
import { ContentPlaceholder } from "scmp-app/components/content/content-placeholder";
import type { contentCoverImageContent$key } from "scmp-app/queries/__generated__/contentCoverImageContent.graphql";

import { CarouselControls } from "./carousel-controls";
import { ImageType, VARIANT_SPECS } from "./constants";
import { getVariantStyledUrl, resolveCarouselOptions } from "./helpers";
import { useCarousel } from "./hooks";
import { Figure, StyledFigcaption, StyledSwiper } from "./styles";
import type { CarouselOptions, CoverImageElement, VariantKey } from "./types";

type Props = {
  className?: string;
  reference: contentCoverImageContent$key;
  responsiveVariants: ResponsiveVariants<VariantKey>;
  shouldShowDefaultImage?: boolean;
  variant?: "plus" | undefined;
  withCaption?: boolean;
  /**
   * Enables the image carousel. `true` prefers the default image type for the
   * first slide; pass `{ imageType }` to prefer a specific image type.
   */
  withCarousel?: CarouselOptions;
};

export const ContentCoverImage: FunctionComponent<Props> = ({
  className,
  reference,
  responsiveVariants,
  shouldShowDefaultImage,
  variant,
  withCaption,
  withCarousel,
}) => {
  const content = useFragment(
    graphql`
      fragment contentCoverImageContent on Content
      @argumentDefinitions(
        withLandscape250x99: { type: "Boolean", defaultValue: false }
        withSize1200x800: { type: "Boolean", defaultValue: false }
        withSize1200x1200: { type: "Boolean", defaultValue: false }
        withSize1280x720: { type: "Boolean", defaultValue: false }
        withSize640x360: { type: "Boolean", defaultValue: false }
        withSize768x768: { type: "Boolean", defaultValue: false }
        withSize540x360: { type: "Boolean", defaultValue: false }
        withSize320x240: { type: "Boolean", defaultValue: false }
        withSize320x180: { type: "Boolean", defaultValue: false }
        withSize118x118: { type: "Boolean", defaultValue: false }
        withSize80x80: { type: "Boolean", defaultValue: false }
        withSize500x500: { type: "Boolean", defaultValue: false }
        withSize800x1200: { type: "Boolean", defaultValue: false }
        withSize660w: { type: "Boolean", defaultValue: false }
        withSize1676x943: { type: "Boolean", defaultValue: false }
        withLeadingSlides: { type: "Boolean", defaultValue: false }
      ) {
        headline
        images {
          url
          title
          type
          landscape250x99: style(filter: { style: "landscape_250_99" })
            @include(if: $withLandscape250x99) {
            url
          }
          size1200x800: style(filter: { style: "1200x800" }) @include(if: $withSize1200x800) {
            url
          }
          size1200x1200: style(filter: { style: "1200x1200" }) @include(if: $withSize1200x1200) {
            url
          }
          size1280x720: style(filter: { style: "wide_landscape" }) @include(if: $withSize1280x720) {
            url
          }
          size640x360: style(filter: { style: "wide_landscape_640_360" })
            @include(if: $withSize640x360) {
            url
          }
          size768x768: style(filter: { style: "768x768" }) @include(if: $withSize768x768) {
            url
          }
          size540x360: style(filter: { style: "540x360" }) @include(if: $withSize540x360) {
            url
          }
          size320x240: style(filter: { style: "320x240" }) @include(if: $withSize320x240) {
            url
          }
          size320x180: style(filter: { style: "320x180" }) @include(if: $withSize320x180) {
            url
          }
          size80x80: style(filter: { style: "80x80" }) @include(if: $withSize80x80) {
            url
          }
          size118x118: style(filter: { style: "118x118" }) @include(if: $withSize118x118) {
            url
          }
          size500x500: style(filter: { style: "500x500" }) @include(if: $withSize500x500) {
            url
          }
          size800x1200: style(filter: { style: "portrait" }) @include(if: $withSize800x1200) {
            url
          }
          size660w: style(filter: { style: "660w" }) @include(if: $withSize660w) {
            url
          }
          size1676x943: style(filter: { style: "wide_landscape_style" })
            @include(if: $withSize1676x943) {
            url
          }
        }
        ... on Article @include(if: $withLeadingSlides) {
          leadingSlides {
            url
            title
            type
            landscape250x99: style(filter: { style: "landscape_250_99" })
              @include(if: $withLandscape250x99) {
              url
            }
            size1200x800: style(filter: { style: "1200x800" }) @include(if: $withSize1200x800) {
              url
            }
            size1200x1200: style(filter: { style: "1200x1200" }) @include(if: $withSize1200x1200) {
              url
            }
            size1280x720: style(filter: { style: "wide_landscape" })
              @include(if: $withSize1280x720) {
              url
            }
            size640x360: style(filter: { style: "wide_landscape_640_360" })
              @include(if: $withSize640x360) {
              url
            }
            size768x768: style(filter: { style: "768x768" }) @include(if: $withSize768x768) {
              url
            }
            size540x360: style(filter: { style: "540x360" }) @include(if: $withSize540x360) {
              url
            }
            size320x240: style(filter: { style: "320x240" }) @include(if: $withSize320x240) {
              url
            }
            size320x180: style(filter: { style: "320x180" }) @include(if: $withSize320x180) {
              url
            }
            size80x80: style(filter: { style: "80x80" }) @include(if: $withSize80x80) {
              url
            }
            size118x118: style(filter: { style: "118x118" }) @include(if: $withSize118x118) {
              url
            }
            size500x500: style(filter: { style: "500x500" }) @include(if: $withSize500x500) {
              url
            }
            size800x1200: style(filter: { style: "portrait" }) @include(if: $withSize800x1200) {
              url
            }
            size660w: style(filter: { style: "660w" }) @include(if: $withSize660w) {
              url
            }
            size1676x943: style(filter: { style: "wide_landscape_style" })
              @include(if: $withSize1676x943) {
              url
            }
          }
        }
      }
    `,
    reference,
  );

  const {
    activeIndex,
    handleDotClick,
    handleNext,
    handlePrevious,
    handleSlideChange,
    swiperReference,
  } = useCarousel();

  // Normalize the boolean shorthand to a stable reference so the memos below
  // don't recompute on every render.
  const carouselOptions = resolveCarouselOptions(withCarousel);

  const firstCarouselImage = useMemo(() => {
    if (!carouselOptions) return null;
    return (
      content.images?.find(image => image?.type === carouselOptions.imageType) ??
      first(content.images) ??
      null
    );
  }, [carouselOptions, content.images]);

  const carouselLeadingSlides = useMemo(
    () => (carouselOptions ? (content.leadingSlides ?? []).filter(notEmpty) : []),
    [carouselOptions, content.leadingSlides],
  );

  const carouselCount = (firstCarouselImage ? 1 : 0) + carouselLeadingSlides.length;
  const hasMultipleImages = carouselCount > 1;

  const mainImage = useMemo(
    () =>
      content.images?.find(image => image?.type === ImageType.MainImage) ?? first(content.images),
    [content.images],
  );

  const displayImage = shouldShowDefaultImage
    ? (content.images?.find(image => image?.type === ImageType.Default) ?? first(content.images))
    : mainImage;

  const mapVariant = (viewportSize: "desktopUp" | "mobileUp" | "tabletUp") => ({
    aspectRatio: VARIANT_SPECS[responsiveVariants[viewportSize]].aspectRatio,
    url: getVariantStyledUrl(displayImage, responsiveVariants[viewportSize]) ?? "",
    width: VARIANT_SPECS[responsiveVariants[viewportSize]].width,
  });

  const mapImageVariant = (
    image: CoverImageElement | null | undefined,
    viewportSize: "desktopUp" | "mobileUp" | "tabletUp",
  ) => ({
    aspectRatio: VARIANT_SPECS[responsiveVariants[viewportSize]].aspectRatio,
    url: getVariantStyledUrl(image, responsiveVariants[viewportSize]) ?? image?.url ?? "",
    width: VARIANT_SPECS[responsiveVariants[viewportSize]].width,
  });

  const responsiveVariant = {
    desktopUp: mapVariant("desktopUp"),
    mobileUp: mapVariant("mobileUp"),
    tabletUp: mapVariant("tabletUp"),
  };

  const { bindResponsiveAspectRatio } = useResponsiveImage(responsiveVariant);
  const imageAlt = findFirstNonEmptyString(displayImage?.title, content.headline) ?? undefined;

  if (hasMultipleImages) {
    return (
      <>
        <StyledSwiper
          className={className}
          modules={[Navigation]}
          onSlideChange={handleSlideChange}
          ref={swiperReference}
          slidesPerView={1}
          {...bindResponsiveAspectRatio}
        >
          {firstCarouselImage && (
            <SwiperSlide key={firstCarouselImage.url ?? "first"}>
              <BaseImage
                $objectFit="cover"
                alt={
                  findFirstNonEmptyString(firstCarouselImage.title, content.headline) ?? undefined
                }
                responsiveVariant={{
                  desktopUp: mapImageVariant(firstCarouselImage, "desktopUp"),
                  mobileUp: mapImageVariant(firstCarouselImage, "mobileUp"),
                  tabletUp: mapImageVariant(firstCarouselImage, "tabletUp"),
                }}
                src={firstCarouselImage.url}
                {...bindResponsiveAspectRatio}
              />
            </SwiperSlide>
          )}
          {carouselLeadingSlides.map(slide => (
            <SwiperSlide key={slide.url}>
              <BaseImage
                $objectFit="cover"
                alt={findFirstNonEmptyString(slide.title, content.headline) ?? undefined}
                lazyload
                responsiveVariant={{
                  desktopUp: mapImageVariant(slide, "desktopUp"),
                  mobileUp: mapImageVariant(slide, "mobileUp"),
                  tabletUp: mapImageVariant(slide, "tabletUp"),
                }}
                src={slide.url}
                {...bindResponsiveAspectRatio}
              />
            </SwiperSlide>
          ))}
        </StyledSwiper>

        <CarouselControls
          activeIndex={activeIndex}
          onDotClick={handleDotClick}
          onNext={handleNext}
          onPrevious={handlePrevious}
          totalSlides={carouselCount}
        />
      </>
    );
  }

  return (
    <Figure {...bindResponsiveAspectRatio} className={className}>
      {displayImage?.url ? (
        <BaseImage
          $objectFit="cover"
          alt={imageAlt}
          responsiveVariant={responsiveVariant}
          src={displayImage?.url}
          {...bindResponsiveAspectRatio}
          lazyload
        />
      ) : (
        <ContentPlaceholder $variant={variant} {...bindResponsiveAspectRatio} />
      )}

      {withCaption && displayImage?.title && (
        <StyledFigcaption>{displayImage?.title}</StyledFigcaption>
      )}
    </Figure>
  );
};

ContentCoverImage.displayName = "ContentCoverImage";
